import { useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { LanguageToggle, useT } from '../i18n';

type Mode = 'signIn' | 'signUp' | 'confirm';

// Minimal sign-in / sign-up screen shown when no creator is authenticated.
// Full-featured screens (projects, courses, etc.) arrive in tasks 16.2-16.7;
// this screen exists to satisfy the authentication gate (Req 1.1, 1.2).
export function SignInScreen(): ReactNode {
  const auth = useAuth();
  const { t } = useT();
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      if (mode === 'signIn') {
        await auth.signIn(email, password);
      } else if (mode === 'signUp') {
        const { confirmationRequired } = await auth.signUp(email, password);
        if (confirmationRequired) {
          setMode('confirm');
        } else {
          await auth.signIn(email, password);
        }
      } else {
        await auth.confirmSignUp(email, code);
        await auth.signIn(email, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.genericError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="auth-language">
        <LanguageToggle />
      </div>
      <form className="auth-card" onSubmit={handleSubmit}>
        <h1>{t('app.title')}</h1>
        <p className="auth-subtitle">
          {mode === 'signIn' && t('auth.subtitle.signIn')}
          {mode === 'signUp' && t('auth.subtitle.signUp')}
          {mode === 'confirm' && t('auth.subtitle.confirm')}
        </p>

        {mode !== 'confirm' && (
          <>
            <label>
              {t('auth.email')}
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              {t('auth.password')}
              <input
                type="password"
                autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
          </>
        )}

        {mode === 'confirm' && (
          <label>
            {t('auth.confirmationCode')}
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </label>
        )}

        {error && <p className="auth-error">{error}</p>}

        <button type="submit" disabled={busy}>
          {busy
            ? t('auth.processing')
            : mode === 'signIn'
              ? t('auth.signIn')
              : mode === 'signUp'
                ? t('auth.signUp')
                : t('auth.confirm')}
        </button>

        {mode === 'signIn' && (
          <button type="button" className="auth-link" onClick={() => setMode('signUp')}>
            {t('auth.noAccount')}
          </button>
        )}
        {mode !== 'signIn' && (
          <button type="button" className="auth-link" onClick={() => setMode('signIn')}>
            {t('auth.backToSignIn')}
          </button>
        )}
      </form>
    </main>
  );
}
